import dayjs from 'dayjs';
import _ from 'lodash';

import { COMMON_DATE_FORMAT, MAX_CALENDAR_DATE, MIN_CALENDAR_DATE } from 'constants/date';
import { DISBURSEMENT_PERIOD_MONTHS } from 'constants/impoundTypes';
import { FrequencyType } from 'constants/loanApplication';
import { formatNumberToCurrencyString } from 'utils/number';

const MONTHS_IN_YEAR = 12;
const ROUNDING_MULTIPLIER = 10;
const MAX_MONTHS_COLLECTED = 12;
const SCHEDULE_MONTH_KEY_FORMAT = 'YYYY-MM';
const CALENDAR_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}/;

// The calendar date of an impound date as a local dayjs date. Impound dates carry no time of day: the
// API sends them as UTC midnight, which read as-is falls on the day before in a US timezone
// (2027-10-01T00:00:00Z is 09/30 there), so a string is read by its YYYY-MM-DD part. A date picked in
// the form is already a local dayjs date.
export const toImpoundCalendarDate = value => {
  const calendarDate = _.isString(value) ? value.match(CALENDAR_DATE_PATTERN)?.[0] : null;

  return dayjs(calendarDate ?? value);
};

export const getCalculatedImpoundFields = ({ monthsCollected, annualAmount }) => {
  const monthlyAmount = Number((annualAmount / MONTHS_IN_YEAR).toFixed(2));

  return {
    monthlyAmount,
    collectedAmount: monthlyAmount * monthsCollected,
  };
};

// isCollected comes from the Fees API; the fallback covers a row the API has not scored yet (a locally
// added row, or a response that predates the flag). Loan Escrow gates first, so escrow off collects nothing.
export const checkIsImpoundCollected = ({ isCollected, isEscrowed }, taxesInsuranceEscrowed) =>
  !!taxesInsuranceEscrowed && (isCollected ?? isEscrowed !== false);

export const checkIsHomeownersInsuranceImpoundType = (
  impoundTypeOption,
  homeownersInsuranceImpoundId,
) =>
  !_.isNil(homeownersInsuranceImpoundId) &&
  impoundTypeOption?.value === homeownersInsuranceImpoundId;

export const getImpoundDetails = ({ impoundTypeName, monthlyAmount, monthsCollected }) =>
  [
    impoundTypeName,
    formatNumberToCurrencyString(monthlyAmount),
    'per month for',
    _.isNil(monthsCollected) ? 'TBD' : monthsCollected,
    'mo.',
  ].join(' ');

export const getMinMaxImpoundDueDate = ({ prepaidInterestCreditFirstPaymentDate }) => {
  return {
    minDate: prepaidInterestCreditFirstPaymentDate
      ? toImpoundCalendarDate(prepaidInterestCreditFirstPaymentDate)
      : dayjs(MIN_CALENDAR_DATE),
    // subtract 1 day to exclude exsessive month from picker
    maxDate: (prepaidInterestCreditFirstPaymentDate
      ? toImpoundCalendarDate(prepaidInterestCreditFirstPaymentDate)
      : dayjs(MAX_CALENDAR_DATE)
    )
      .add(DISBURSEMENT_PERIOD_MONTHS, 'month')
      .subtract(1, 'day'),
  };
};

export const roundToFixedFloor = (num, decimalPoint) =>
  num < 0
    ? -Math.round(-num * ROUNDING_MULTIPLIER ** decimalPoint) / ROUNDING_MULTIPLIER ** decimalPoint
    : Math.round(num * ROUNDING_MULTIPLIER ** decimalPoint) / ROUNDING_MULTIPLIER ** decimalPoint;

export const calculateNextAmountDueDate = ({ amountDueDates }) => {
  const nextAmountDueDate = _.minBy(
    amountDueDates.filter(({ dueDate }) => dayjs(dueDate).diff() > 0),
    'dueDate',
  );

  return {
    amountDue: nextAmountDueDate
      ? formatNumberToCurrencyString(nextAmountDueDate.amountDue)
      : 'N/A',
    dueDate: nextAmountDueDate
      ? dayjs(nextAmountDueDate.dueDate).format(COMMON_DATE_FORMAT)
      : 'N/A',
  };
};

export const checkIsInvalidImpound = ({
  impound,
  prepaidInterestCreditFirstPaymentDate,
  ignoreMonthCollected,
  isEscrowApplicable = true,
}) => {
  const { amountDueDates, monthsCollected, isEscrowed } = impound ?? {};

  const isEscrowValidationEnabled = isEscrowApplicable && isEscrowed !== false;

  if (!isEscrowValidationEnabled && _.isEmpty(amountDueDates)) {
    return false;
  }

  const { minDate, maxDate } = getMinMaxImpoundDueDate({ prepaidInterestCreditFirstPaymentDate });

  // An impound with no due date cannot be scheduled for disbursement (mirrors ImpoundsScheduler).
  const isAnyDueDateOutOfRange =
    _.isEmpty(amountDueDates) ||
    amountDueDates.some(({ dueDate }) => !dayjs(dueDate).isBetween(minDate, maxDate, 'day', '[]'));

  const isMonthsCollectedMissing =
    !ignoreMonthCollected && isEscrowValidationEnabled && _.isNil(monthsCollected);

  return isAnyDueDateOutOfRange || isMonthsCollectedMissing;
};

export const checkIsInvalidImpounds = (
  impounds,
  {
    prepaidInterestCreditFirstPaymentDate,
    ignoreMonthCollected = false,
    isEscrowApplicable = true,
  },
) => {
  return (
    impounds.findIndex(({ amountDueDates, monthsCollected, isEscrowed }) =>
      checkIsInvalidImpound({
        impound: { amountDueDates, monthsCollected, isEscrowed },
        prepaidInterestCreditFirstPaymentDate,
        ignoreMonthCollected,
        isEscrowApplicable,
      }),
    ) >= 0
  );
};

export const getSchedule = ({
  monthsCushion,
  impounds,
  prepaidInterestCreditFirstPaymentDate: firstPaymentDate,
}) => {
  const totalCollectedAmount = _.sumBy(impounds, i => Number(i.collectedAmount));
  const totalMonthlyAmount = _.sumBy(impounds, i => roundToFixedFloor(Number(i.monthlyAmount), 2));

  const firstPaymentDateWithoutShift = toImpoundCalendarDate(firstPaymentDate);

  const initialDisbursementsByMonths = Array.from({ length: DISBURSEMENT_PERIOD_MONTHS }).reduce(
    (acc, __, index) => {
      const monthDate = dayjs(firstPaymentDateWithoutShift).add(index, 'month');

      const year = monthDate.format('YYYY');
      const month = monthDate.format('MMMM');
      const key = monthDate.format('YYYY-MM');

      acc[key] = { disbursement: 0, description: [], month, year };

      return acc;
    },
    {},
  );

  const disbursementsByMonths = impounds.reduce((acc, { impoundTypeName, amountDueDates }) => {
    amountDueDates.forEach(({ amountDue, dueDate }) => {
      const key = toImpoundCalendarDate(dueDate).format(SCHEDULE_MONTH_KEY_FORMAT);

      if (acc[key]) {
        acc[key].disbursement += Number(amountDue);
        acc[key].description.push(
          `${impoundTypeName} (${formatNumberToCurrencyString(amountDue)})`,
        );
      }
    });

    return acc;
  }, initialDisbursementsByMonths);

  const schedule = _.values(disbursementsByMonths).reduce((acc, item, index) => {
    const { disbursement, description } = item;
    const isStartMonth = !index;

    const prevMonthBalance = acc[index - 1]?.balance;

    const startBalance = totalCollectedAmount + totalMonthlyAmount - disbursement;
    const currentBalance = prevMonthBalance + totalMonthlyAmount - disbursement;

    acc.push({
      ...item,
      payment: totalMonthlyAmount,
      description: description?.join(', '),
      balance: isStartMonth ? startBalance : currentBalance,
    });

    return acc;
  }, []);

  const totalCushion = monthsCushion * totalMonthlyAmount || 0;
  const minMonthBalance = _.minBy(schedule, 'balance')?.balance;

  const aggregateAdjustment = Math.min(totalCushion - minMonthBalance, 0);

  return {
    schedule: schedule.map(i => ({ ...i, balance: i.balance + aggregateAdjustment })),
    aggregateAdjustment,
  };
};

// Mirrors ImpoundsScheduler.GetMonthsToCollect in skynet.pricing: the fewest months of the monthly
// amount to collect at closing so this impound alone never drops below the cushion on the aggregate
// schedule. A shortfall under a hundredth of a month (cents from rounding the monthly amount) is ignored.
// Null when the impound cannot be scheduled.
export const getMonthsToCollect = ({
  annualAmount,
  amountDueDates,
  monthsCushion,
  prepaidInterestCreditFirstPaymentDate: firstPaymentDate,
}) => {
  const { monthlyAmount } = getCalculatedImpoundFields({ annualAmount: annualAmount || 0 });

  if (!firstPaymentDate || !(monthlyAmount > 0) || _.isEmpty(amountDueDates)) {
    return null;
  }

  const periodStart = toImpoundCalendarDate(firstPaymentDate).startOf('day');
  const periodEnd = periodStart.add(DISBURSEMENT_PERIOD_MONTHS, 'month');

  const dues = amountDueDates.map(({ dueDate, amountDue }) => ({
    dueDate: dueDate ? toImpoundCalendarDate(dueDate) : null,
    amountDue,
  }));

  const isAnyDueInvalid = dues.some(
    ({ dueDate, amountDue }) =>
      _.isNil(amountDue) ||
      !dueDate?.isValid() ||
      !dueDate.isBetween(periodStart, periodEnd, 'day', '[]'),
  );

  if (isAnyDueInvalid) {
    return null;
  }

  const lowestBalance = _.range(DISBURSEMENT_PERIOD_MONTHS).reduce(
    ({ balance, lowest }, index) => {
      const monthKey = periodStart.add(index, 'month').format(SCHEDULE_MONTH_KEY_FORMAT);
      const disbursement = _.sumBy(
        dues.filter(({ dueDate }) => dueDate.format(SCHEDULE_MONTH_KEY_FORMAT) === monthKey),
        ({ amountDue }) => Number(amountDue),
      );
      const nextBalance = balance + monthlyAmount - disbursement;

      return { balance: nextBalance, lowest: Math.min(lowest, nextBalance) };
    },
    { balance: 0, lowest: Infinity },
  ).lowest;

  const shortfallMonths = Number(
    (((monthsCushion || 0) * monthlyAmount - lowestBalance) / monthlyAmount).toFixed(2),
  );

  return _.clamp(Math.ceil(shortfallMonths), 0, MAX_MONTHS_COLLECTED);
};

export const getFormattedAggregateAdjustment = ({
  prepaidInterestCreditFirstPaymentDate,
  aggregateAdjustment,
  isInvalidImpoundExists,
}) => {
  return !prepaidInterestCreditFirstPaymentDate || isInvalidImpoundExists
    ? 'TBD'
    : formatNumberToCurrencyString(aggregateAdjustment || 0);
};

export const getAmountDueDates = ({ formState, firstPaymentDate, isResultArray = false }) => {
  const isMonthlyFrequency = formState.frequency.value === FrequencyType.MONTHLY;

  const baseAmount = roundToFixedFloor(
    formState.annualAmount / formState.frequency.secondaryValue,
    2,
  );
  const leftoverAmount = Number(
    (formState.annualAmount - baseAmount * formState.frequency.secondaryValue).toFixed(2),
  );

  const lastAmount = Number((baseAmount + leftoverAmount).toFixed(2));

  if (isResultArray) {
    return _.range(formState.frequency.secondaryValue).reduce((acc, __, index) => {
      const isLast = index === formState.frequency.secondaryValue - 1;

      acc.push({
        dueDate:
          firstPaymentDate && isMonthlyFrequency
            ? toImpoundCalendarDate(firstPaymentDate).add(index, 'month')
            : null,
        amountDue: isLast ? lastAmount : baseAmount,
      });

      return acc;
    }, []);
  }

  return {
    ..._.range(formState.frequency.secondaryValue).reduce((acc, __, index) => {
      const isLast = index === formState.frequency.secondaryValue - 1;

      acc[`amountDueDates${index}dueDate`] =
        firstPaymentDate && isMonthlyFrequency
          ? toImpoundCalendarDate(firstPaymentDate).add(index, 'month')
          : null;
      acc[`amountDueDates${index}amountDue`] = isLast ? lastAmount : baseAmount;

      return acc;
    }, {}),
  };
};
